import moment from "moment";
import { Method, SearchKind } from "~/enums";
import { CodeDto, EquipDto, EquipInfo, EventInfo, FaultTrendInfo, OperEquipStatus, OperEquipStatusByRegion, ProdByEngSource, ProdInfoStatus, ProdTrendsInfo, SiteInfo, UserInfo } from "~/models/dto-types";
import tocRouteList from "@/utils/toc-route_list.json";
import { useUserStore } from "./userStore";
import { useCommonCodeStore } from "./commonCodeStore";
import { useLocalStorage, useSessionStorage } from "@vueuse/core";
import axios from 'axios';
import ASTempData from './asTempData.json';
import { PageInfo } from "~/models/core-types";
import Swal from "sweetalert2";

type SitePersonalizationCacheItem = {
  siteSysTitle?: string | null;
  sitelogoSeq?: number | null;
  attachPath?: string | null;
  attachName?: string | null;
  enabled?: boolean | null;
};

interface selectRouteForm{
  path: string,
  name: string,
  icon: string,
  child: Array<this>
}

export const useTocStore = defineStore('tocStore', {
  state: () => ({
    dashboardProd: useLocalStorage("dashboardProd", {} as any),
    tocEquipList: useSessionStorage("tocEquipList", [] as EquipDto[]),
    defaultCity: useLocalStorage("defaultCity", '' as string | undefined),
    defaultSigungu: useLocalStorage("defaultSigungu", '' as String | undefined),
    selectCity: useSessionStorage("selectCity", '' as string),
    selectSigungu: useSessionStorage("selectSigungu" ,'' as string),
    tocRouteList: tocRouteList,
    tableViewPageNumber: 0,
    selectSiteInfo: useSessionStorage('selectSiteInfo', {} as SiteInfo),
    selectEquipInfo: useSessionStorage('selectEquipInfo', {} as EquipInfo),
    dashboardPageType: useSessionStorage('dashboardPageType', {} as string),
    mainRoute: useSessionStorage("mainRoute", {} as selectRouteForm),
    subRouteList: useSessionStorage("subRouteList", [] as selectRouteForm[] | null),
    depth1Route: useSessionStorage("depth1Route", {} as selectRouteForm),
    depth2Route: useSessionStorage("depth2Route", null as selectRouteForm | null),
    depth3Route: useSessionStorage("depth3Route", null as selectRouteForm | null),
    // 임시 이미지 저장소 (API 개발 전까지 사용)
    schematicImages: useSessionStorage<Record<string, string>>("schematicImages", {} as Record<string, string>),
    // 사이트 개인화 캐시(로고/타이틀) - 사이트 전환 시 즉시 반영용
    sitePersonalizationCache: useSessionStorage<Record<string, SitePersonalizationCacheItem>>(
      "sitePersonalizationCache",
      {} as Record<string, SitePersonalizationCacheItem>
    ),
    // 설비지도 지역 선택 필터 상태 — 다른 페이지 이동 후 복귀 시 복원용
    equipMapFilterState: useSessionStorage<any>('equipMapFilterState', null),
  }),
  getters: {},
  actions: {
    saveEquipMapFilterState(state: any) {
      this.equipMapFilterState = state;
    },
    async getProdOverall(): Promise<any> {
      const res = await useCustomFetch('/api/v1/prod/overall', { method: Method.GET });
      return res.data.value;
    },
    setSelectEquipInfo(equipInfo: EquipInfo){
      this.selectEquipInfo = equipInfo
    },
    setSelectSiteInfo(siteInfo: SiteInfo){
      this.selectSiteInfo = siteInfo;
    },

    // 임시 이미지 저장 (API 개발 전까지 사용)
    setSchematicImage(cid: string, imageDataUrl: string) {
      const schematicImagesRef = (this as any).schematicImages;
      // useLocalStorage는 ref를 반환하므로 .value로 접근
      if (!schematicImagesRef?.value) {
        schematicImagesRef.value = {} as Record<string, string>;
      }
      schematicImagesRef.value[cid] = imageDataUrl;
    },
    // 임시 이미지 조회 (API 개발 전까지 사용)
    getSchematicImage(cid: string): string | null {
      const schematicImagesRef = (this as any).schematicImages;
      if (!schematicImagesRef || !cid) {
        return null;
      }
      // useLocalStorage는 ref를 반환하므로 .value로 접근
      return schematicImagesRef.value?.[cid] || null;
    },
    // 임시 이미지 삭제 (API 개발 전까지 사용)
    removeSchematicImage(cid: string) {
      const schematicImagesRef = (this as any).schematicImages;
      if (!schematicImagesRef || !cid) {
        return;
      }
      // useLocalStorage는 ref를 반환하므로 .value로 접근
      if (schematicImagesRef.value) {
        delete schematicImagesRef.value[cid];
      }
    },
    setTableViewPageNumber(num: number){
      this.tableViewPageNumber = num;
    },
    resetTableViewPageNumber(){
      this.tableViewPageNumber = 0;
    },
    async getEquipList() {
			const res = await useCustomFetch<EquipDto[]>(`/api/v1/equips`, 
        { method: Method.GET, query: { pageNo: 1, pageSize: 5000 } });
			
      this.defaultCity = undefined;
      this.defaultSigungu = undefined;

      this.tocEquipList = res.data.value.content as EquipDto[];

      let equipCitys = this.tocEquipList.map(v => v.dongCode?.substring(0, 2) ?? null);
      let equipSigungus = this.tocEquipList.map(v => v.dongCode?.substring(2, 5) ?? null);

      equipCitys = [...new Set(equipCitys)];
      equipSigungus = [... new Set(equipSigungus)];

      if(equipCitys.length === 1){
        this.defaultCity = equipCitys[0] as string;
      }
      if(equipSigungus.length === 1){
        this.defaultSigungu === equipSigungus[0] as string;
      }

			return this.tocEquipList;
		},
    async getEquipListOnParam(
      param?:{
        pageNo: number,
        pageSize: number,
        cityCode?: string,
        sigunguCode?: string,
        dongCode?: string,
        prjKind?: string,
        buildingUsage?: string,
      }
    ) {
      const p = param ?? { pageNo: 1, pageSize: 20 };
			const res = await useCustomFetch<EquipDto[]>(`/api/v1/equips`, 
        { method: Method.GET, query: { pageNo: p.pageNo, pageSize: p.pageSize} });
			
      // this.defaultCity = undefined;
      // this.defaultSigungu = undefined;

      // this.tocEquipList = res.data.value.content as EquipDto[];

      // let equipCitys = this.tocEquipList.map(v => v.dongCode?.substring(0, 2) ?? null);
      // let equipSigungus = this.tocEquipList.map(v => v.dongCode?.substring(2, 5) ?? null);

      // equipCitys = [...new Set(equipCitys)];
      // equipSigungus = [... new Set(equipSigungus)];

      // if(equipCitys.length === 1){
      //   this.defaultCity = equipCitys[0] as string;
      // }
      // if(equipSigungus.length === 1){
      //   this.defaultSigungu === equipSigungus[0] as string;
      // }

			return res.data.value;
		},
		async tocDashboardProdData(cityCode: string, sigunguCode: string) {
      // let city = this.defaultCity === '' ? cityCode : this.defaultCity
    // let sigungu = this.defaultSigungu === '' ? sigunguCode : this.defaultSigungu

        const prodRes = await useCustomFetch(`/api/v1/prod/overall`, { method: Method.GET});

        let result = prodRes.data.value as ProdInfoStatus;
   
     
        return result;
		},
    async tocDashboardfaultData(cityCode: string, sigunguCode: string, searchDateType: number){
      let city = this.defaultCity === '' ? cityCode : this.defaultCity
      let sigungu = this.defaultSigungu === '' ? sigunguCode : this.defaultSigungu
      let dateType = searchDateType ? searchDateType : 0;


      let startDate = moment().format('YYYY-MM-DD');
      let endDate = moment().format('YYYY-MM-DD');

      if(dateType === 0){
        startDate = moment().startOf('day').format('YYYY-MM-DD');
        endDate = moment().endOf('day').format('YYYY-MM-DD');
      } else if(dateType === 1){
        startDate = moment().startOf('month').format('YYYY-MM-DD');
        endDate = moment().endOf('month').format('YYYY-MM-DD');
      } else if(dateType === 2){
        startDate = moment().startOf('year').format('YYYY-MM-DD');
        endDate = moment().endOf('year').format('YYYY-MM-DD');
      }
      let faultRes = await useCustomFetch(`/api/v1/event/fault-trend`, 
        { method: Method.GET,  
          query:{
            cityCode: city, 
            sigunguCode: sigungu, 
            searchDateType: dateType, 
            startDate: startDate, 
            endDate: endDate} 
          });

      let result = faultRes.data.value as FaultTrendInfo;


      return result;
  },
    async tocDashboarEventData(eventType: string) {
			try {
				const eventRes = await useCustomFetch(`/api/v1/event/overview`, 
          { method: Method.GET,
            query:{eventType: eventType} });

				let result = eventRes.data.value as EventInfo[];

				// null 또는 undefined 체크 추가 (404 응답 시 빈 배열 반환)
				return result ?? [];
			} catch (error) {
				console.error('이벤트 데이터 조회 실패:', error);
				return [];
			}
		},
    async tocDashboardTrendData(cityCode: string, sigunguCode: string, searchDateType: number) {
      let city = this.defaultCity === '' ? cityCode : this.defaultCity
      let sigungu = this.defaultSigungu === '' ? sigunguCode : this.defaultSigungu
      let dateType = searchDateType ? searchDateType : 0;

      let startDate = moment().format('YYYY-MM-DD');
      let endDate = moment().format('YYYY-MM-DD');
      
      if(dateType === 0){
        startDate = moment().startOf('day').format('YYYY-MM-DD');
        endDate = moment().endOf('day').format('YYYY-MM-DD');
      } else if(dateType === 1){
        startDate = moment().startOf('month').format('YYYY-MM-DD');
        endDate = moment().endOf('month').format('YYYY-MM-DD');
      } else if(dateType === 2){
        startDate = moment().startOf('year').format('YYYY-MM-DD');
        endDate = moment().endOf('year').format('YYYY-MM-DD');
      }

			const trendRes = await useCustomFetch(`/api/v1/prod/trend/engsource`, 
        { method: Method.GET,  
          query:
          {
            cityCode: city, 
            sigunguCode: sigungu, 
            searchDateType: dateType, 
            startDate: startDate, 
            endDate: endDate
          } 
        });

			let result = trendRes.data.value as ProdTrendsInfo;

			return result;
		},

    async getZloc(cityCode: string, sigunguCode: string | undefined = undefined, dongCode: string | undefined = undefined){
      const result = await useCustomFetch(`/api/v1/common/zloc`, 
        { method: Method.GET, 
          query: {
            cityCode: cityCode, 
            sigunguCode: sigunguCode, 
            dongCode: dongCode
          }});
      return result.data.value;
    },

    async setRouteForm(fromPath:string, toPath: string, checkPath: string[]){
      if(!toPath) return;

      const userStore = useUserStore();
      const commonCodeStore = useCommonCodeStore();
      const engSourceMap = (commonCodeStore.getMapCodeList('engSource') as Map<string, CodeDto>);
      const engSourceList = engSourceMap.values();

      const routeList = this.tocRouteList;
      const userRoute = routeList[userStore.getUserType as keyof typeof routeList];

    
      let splitPath = toPath.split("/");

      let findMainRoute = userRoute.find(v => v.path.split('/').some(t => splitPath.includes(t)));
      
      if(findMainRoute){
        this.mainRoute = findMainRoute as selectRouteForm;
      }
    },

    async getTocEquipMapInfo(parameter: {
      cityCode?: string,
      sigunguCode?: string,
      dongCode?: string,
      prjKind?: string,
      buildingUsage?: string,
      prjRegYear?: string,
      engSource?: string,
      searchKind?: string,
      searchText?: string
    }){


      const result = await useCustomFetch(`/api/v1/monitor/map`, {method: Method.GET, query: {
        cityCode        : parameter.cityCode,
        sigunguCode     : parameter.sigunguCode,
        dongCode        : parameter.dongCode,
        prjKind         : parameter.prjKind,
        buildingUsage   : parameter.buildingUsage,
        prjRegYear      : parameter.prjRegYear,
        engSource       : parameter.engSource,
        searchKind      : parameter.searchKind,
        searchText      : parameter.searchText
      }});

      return result.data.value;

    },

    
    /**
    * 
    * @param pageNo // 조회 페이지 번호
    * @param pageSize // 조회 페이지수
    * @param cityCode // 시도
    * @param sigunguCode // 시군구
    * @param dongCode // 읍면동
    * @param mapLevel // 맵레벨
    * @param mapDongCode //지도상 현재 위치 법정동 코드
    * @param prjKind //사업구분코드
    * @param buildingUsage //건물용도
    * @param prjRegYear // 사업용도
    * @param engSource // 에너지원
    * @param searchKind // 검색 타입(설비번호 (10), 설치자명(20), 사이트명(30))
    * @param searchText // 검색어
    */
    async getTocEquipDataList(
      parameter: {
        pageNo:         number,
        pageSize:       number,
        cityCode:       string | undefined,
        sigunguCode:    string | undefined,
        dongCode:       string | undefined,
        equipStatus:    string | undefined,
        mapLevel:       number | undefined,
        mapDongCode:    string | undefined,
        prjKind:        string | undefined,
        buildingUsage:  string | undefined,
        prjRegYear:     string | undefined,
        engSource:      string | undefined,
        searchKind:     string | undefined,
        searchText:     string | undefined
      }
    ){
      const result = await useCustomFetch('/api/v1/equips', 
      {method: Method.GET, query: {
        pageNo          :parameter.pageNo, 
        pageSize        :parameter.pageSize, 
        cityCode        :parameter.cityCode,
        sigunguCode     :parameter.sigunguCode, 
        dongCode        :parameter.dongCode, 
        equipStatus     :parameter.equipStatus,
        mapLevel        :parameter.mapLevel, 
        mapDongCode     :parameter.mapDongCode, 
        prjKind         :parameter.prjKind, 
        buildingUsage   :parameter.buildingUsage, 
        prjRegYear      :parameter.prjRegYear, 
        engSource       :parameter.engSource, 
        searchKind      :parameter.searchKind, 
        searchText      :parameter.searchText
      }});


      return result.data.value;
    },

    async getEquipProdList(parameter: {
      pageNo: number,
      pageSize: number,
      engSource?: string;
      equipStatus?: string;
      cityCode?: string;
      sigunguCode?: string;
      dongCode?: string;
      prjKind?: string;
      prjRegYear?: string;
      searchKind?: string;
      searchText?: string;
      searchDateType?: number;
      startDate?: string;
      endDate?: string,
      sort?: string
    }){

      const result = await useCustomFetch(`/api/v1/prod/list`, {
        method: Method.GET,
        query: {
          pageNo          : parameter.pageNo,
          pageSize        : parameter.pageSize,
          engSource       : parameter.engSource,
          equipStatus     : parameter.equipStatus,
          cityCode        : parameter.cityCode,
          sigunguCode     : parameter.sigunguCode,
          dongCode        : parameter.dongCode,
          prjKind         : parameter.prjKind,
          prjRegYear      : parameter.prjRegYear,
          searchKind      : parameter.searchKind,
          searchText      : parameter.searchText,
          searchDateType  : parameter.searchDateType,
          startDate       : parameter.startDate,
          endDate         : parameter.endDate,
          sort            : parameter.sort
        }
      });

      return result.data.value;
    },


    async getEquipProdTrendList(parameter: {
      engSource?: string;
      equipStatus?: string;
      cityCode?: string;
      sigunguCode?: string;
      dongCode?: string;
      prjKind?: string;
      prjRegYear?: string;
      searchKind?: string;
      searchText?: string;
      searchDateType?: number;
      startDate?: string;
      endDate?: string,
    }){

      // let param = {};
      // param = parameter;
      // param.startDate = parameter.searchStartDateString;
      // param.endDate = parameter.searchEndDateString;

      const result = await useCustomFetch(`/api/v1/prod/trend/datetime`, {
        method: Method.GET, query: {
          engSource       : parameter.engSource,
          equipStatus     : parameter.equipStatus,
          cityCode        : parameter.cityCode,
          sigunguCode     : parameter.sigunguCode,
          dongCode        : parameter.dongCode,
          prjKind         : parameter.prjKind,
          prjRegYear      : parameter.prjRegYear,
          searchKind      : parameter.searchKind,
          searchText      : parameter.searchText,
          searchDateType  : parameter.searchDateType,
          startDate       : parameter.startDate,
          endDate         : parameter.endDate,
        }
      });

      return result.data.value;
    },
    async getSiteList(parameter: {
      pageNo          : number,
      pageSize        : number,
      cityCode?       : string,
      sigunguCode?    : string,
      dongCode?       : string,
      prjKind?        : string,
      buildingUsage?  : string,
      prjRegYear?     : string,
      engSource?      : string,
      searchKind?     : string,
      searchText?     : string
    }){
      try{
        const result = await useCustomFetch('/api/v1/sites', {method: Method.GET, query: {
          pageNo        :parameter.pageNo,
          pageSize      :parameter.pageSize,
          cityCode      :parameter.cityCode,
          sigunguCode   :parameter.sigunguCode,
          dongCode      :parameter.dongCode,
          prjKind       :parameter.prjKind,
          buildingUsage :parameter.buildingUsage,
          prjRegYear    :parameter.prjRegYear,
          engSource     :parameter.engSource,
          searchKind    :parameter.searchKind,
          searchText    :parameter.searchText
         }});
         
         if(result.data.value){
          return result.data.value;
         } else {
          throw new Error(result.error.value?.data.message);
         }

      } catch(e){
        Swal.fire({
          html: `<p style="font-size: 15px;">오류가 발생하였습니다.<br>잠시 후 다시 시도해주세요.</p>`,
          confirmButtonColor: "#1949e9",
          confirmButtonText: "확인",
          width: "85%",
        })
      }
 
    },
    async getOwnerSiteInfo(ownerSeq: number, siteName: string){
      const result = await useCustomFetch(`/api/v1/site`, 
        {method: Method.GET, 
          query: {ownerSeq: ownerSeq, siteName: siteName}});

      return result.data.value;
    },
    
    async getSiteDetailInfo(ownerSeq: number, engSource: string, siteName: string){
      const result = await useCustomFetch(`/api/v1/prod/site`,
         {method: Method.GET, 
          query: {ownerSeq: ownerSeq, engSource: engSource, siteName: siteName}});

      return result.data.value;
    },

    async getSiteEquipProdInfo(ownerSeq: number, engSource: string, siteName: string, searchDateType: number, startDate: string, endDate: string){
      const result = await useCustomFetch(`/api/v1/prod/trend/equips`, 
        {method: Method.GET, 
          query: 
          {
            ownerSeq: ownerSeq, 
            engSource: engSource, 
            siteName: siteName, 
            searchDateType: searchDateType, 
            startDate: startDate, 
            endDate: endDate
          }});

      return result.data.value;
    },
    async getEquipMonitorInfoData(cid: string){
      if(!cid){
        return null;
      }

      try{
        const res = await useCustomFetch(`/api/v1/equips/${cid}/monitoring-data`, { method: Method.GET });

        if(res.data.value){
          return res.data.value;
        }

        return null;
      } catch (error){
        console.error(`[계통도 데이터] cid: ${cid} - API 호출 오류:`, error);
        return null;
      }
    },
    async getTocEventList(parameter: {
      pageNo: number,
      pageSize: number,
      searchDateType: number,
      startDate: string,
      endDate: string,
      eventType: string,
      eventProcessKind :number,
      cityCode?: string,
      sigunguCode?: string,
      dongCode?: string,
      prjKind?: string,
      buildingUsage?: string,
      prjRegYear?: string,
      engSource?: string,
      searchKind?: string,
      searchText?: string,
      sort?: string
    }){
      const result = await useCustomFetch(`/api/v1/events`, {method: Method.GET, query: {
        pageNo          : parameter.pageNo,
        pageSize        : parameter.pageSize,
        searchDateType  : parameter.searchDateType,
        startDate       : parameter.startDate,
        endDate         : parameter.endDate,
        eventType       : parameter.eventType,
        eventProcessKind: parameter.eventProcessKind,
        cityCode        : parameter.cityCode,
        sigunguCode     : parameter.sigunguCode,
        dongCode        : parameter.dongCode,
        prjKind         : parameter.prjKind,
        buildingUsage   : parameter.buildingUsage,
        prjRegYear      : parameter.prjRegYear,
        engSource       : parameter.engSource,
        searchKind      : parameter.searchKind,
        searchText      : parameter.searchText,
        sort            : parameter.sort
      }});
  
      return result.data.value;
    },

    async getTocSearchCondi(parameter: {
      kind: string,
      regionKind: string,
      regionName?: string,
      regionCode?: string
    }){


      const result = await useCustomFetch('/api/v1/common/searchBarBaseInfo', {method: Method.GET, query: {
        kind        : parameter.kind,
        regionKind  : parameter.regionKind,
        regionName  : parameter.regionName,
        regionCode  : parameter.regionCode
      }});
      
      return result.data.value;
    },

    async getTocAnalysisOwner(parameter: {
      pageNo: number,
      pageSize: number,
      cityCode?: string,
      sigunguCode?: string,
      dongCode?: string,
      ownerName?: string
    }){

      if(parameter.ownerName?.trim() === '' || (parameter.ownerName?.length ?? 0) < 2){
        parameter.ownerName = undefined
      }

      const result = await useCustomFetch(`/api/v1/owners`, {method: Method.GET, query: {
        pageNo      : parameter.pageNo,
        pageSize    : parameter.pageSize,
        cityCode    : parameter.cityCode,
        sigunguCode : parameter.sigunguCode,
        dongCode    : parameter.dongCode,
        ownerName   : parameter.ownerName
      }});

      return result.data.value;
    },

    async getTocAnalysisOwnerEquip(ownerName: string){
      // const result = await useCustomFetch(`/api/v1/owners/${ownerSeq}`);
      const result = await useCustomFetch(`/api/v1/equips`, {method: Method.GET, query: {
        pageNo: 1,
        pageSize: 50,
        searchKind: '20',
        searchText: ownerName
      }});

      return result.data.value.content;
    },

    async getTocAnalysisOwnerEquipBySeq(ownerSeq: number){
      const result = await useCustomFetch(`/api/v1/m/equips`, {method: Method.GET, query: {
        ownerSeq: ownerSeq
      }});

      return result.data.value;
    },


    async getTocAnalysisOwnerEquipData(parameter: {
      searchDateType: number,
      startDate: string,
      endDate: string,
      cid: string
    }){

      const result = await useCustomFetch(`/api/v1/prod/info`, {method: Method.GET, query: {
        searchDateType  : parameter.searchDateType,
        startDate       : parameter.startDate,
        endDate         : parameter.endDate,
        cid             : parameter.cid
      }});

    /**
     * 데이터 array Info
     * 0 : 발전량
     * 1 : 사용량
     * 2 : CO2 저감량
     * 3 : 발전 효율
     * 4 : 인버터 효율
     * 5 : 사용 효율
     * 6 : 사용 COP
     * 7 : 발전 시간
     * 8 : 이전 날짜 발전량
     * 9 : 이전 날짜 발전시간
     */

      return result.data.value;
    },
    async getTocCompareOwnerData(parameter: {
      pageNo      : number,
      pageSize    : number,
      engSource   : string,
      cityCode    : string,
      sigunguCode : string,
      dongCode    : string,
      searchDate  : string,
      sort        : string
    }){
      const result = await useCustomFetch(`/api/v1/toc/analysis/compare/owner`, {method: Method.GET, query:{
        pageNo      : parameter.pageNo,
        pageSize    : parameter.pageSize,
        engSource   : parameter.engSource,
        cityCode    : parameter.cityCode,
        sigunguCode : parameter.sigunguCode,
        dongCode    : parameter.dongCode,
        searchDate  : parameter.searchDate,
        sort        : parameter.sort
      }});

      return result.data.value;
    },

    async getTocCompareOwnerPageData(parameter: {
      pageNo      : number,
      pageSize    : number,
      engSource   : string,
      cityCode    : string,
      sigunguCode : string,
      dongCode    : string,
      searchDate  : string,
      sort        : string
    }){
      const result = await useCustomFetch(`/api/v1/toc/analysis/compare/owner/page`, {method: Method.GET, query:{
        pageNo      : parameter.pageNo,
        pageSize    : parameter.pageSize,
        engSource   : parameter.engSource,
        cityCode    : parameter.cityCode,
        sigunguCode : parameter.sigunguCode,
        dongCode    : parameter.dongCode,
        searchDate  : parameter.searchDate,
        sort        : parameter.sort
      }});

      return result.data.value;
    },
    async getTocCompareRegionData(parameter: {
      engSource   : string,
      cityCode    : string,
      sigunguCode : string,
      searchDate  : string,
      sort: string
    }){
      const result = await useCustomFetch(`/api/v1/toc/analysis/compare/area`, {method: Method.GET, query: {
        engSource     : parameter.engSource,
        cityCode      : parameter.cityCode,
        sigunguCode   : parameter.sigunguCode,
        searchDate    : parameter.searchDate,
        sort          : parameter.sort
      }});
      return result.data.value;
    },

    async getTocNoticeData(){
      const result = await useFetch('/api/v1/boards/notices',
         {method: Method.GET, 
          query: {sysKind: 'pc', size: 100}});

      return result;
    },
    async getTocNoticeDetailData(noticeSeq: number){
      const result = await useFetch(`/api/v1/boards/notices/r/${noticeSeq}`, {method: Method.GET});

      return result.data.value;
    },
    async getTocQnaData(userSeq: number){
      const result = await useCustomFetch(`/api/v1/boards/qnas`,
         {method: Method.GET, 
          query: {sysKind:'pc', size: '100', userSeq: userSeq}});
      // const result = await useCustomFetch(`/api/v1/boards/qnas`, {method: Method.GET, query: {sysKind:'pc', size: '100', userSeq: userSeq}});

      return result.data.value;
    },
    async getTocQnaDetailData(qnaSeq: number){
      const result = await useCustomFetch(`/api/v1/boards/qnas/${qnaSeq}`, {method: Method.GET});

      return result.data.value;
    },
    async setTocQna(qnaFormData: FormData){
      const res = await useCustomFetch('/api/v1/boards/qnas/regist', {
          method: Method.POST,
          body: qnaFormData,
      })
      return res;
    },
    async setTocQnaAnswer(qnaSeq: number, answerData: any){
      const res = await useCustomFetch(`/api/v1/boards/qnas/${qnaSeq}`, {method: Method.PUT, body: answerData});

      return res;
    },
        async getAttachment(attachSeq: number): Promise<Blob>{
      const res = await useCustomFetch<Blob>(`/api/v1/boards/qnas/attach`, 
        {method: Method.GET, query: {attachSeq: attachSeq}, responseType: 'blob'});
      
      return res.data.value as Blob;
    },
    async getManagerInfos(userSeq: number){
      const res = await useCustomFetch('/api/v1/setting/manager',
         {method: Method.GET, query:{userSeq: userSeq}});

      return res.data.value;
    },
    async createManagerInfo(parameter: {
      accountStatus:    string,
      area:             string,
      areaKind:         string,
      authGroupSeq:     string,
      companyKind:      string,
      companySeq:       string,
      email?:           string,
      isAdmin:          string,
      mobile?:          string,
      phone?:           string,
      regId?:           number,
      userId?:          string,
      userName?:        string,
      userPassword:     string,
    }){
      const res = await useCustomFetch('/api/v1/setting/manager', {method:Method.POST, body: {
        accountStatus:    parameter.accountStatus,
        area:             parameter.area,
        areaKind:         parameter.areaKind,
        authGroupSeq:     parameter.authGroupSeq,
        companyKind:      parameter.companyKind,
        companySeq:       parameter.companySeq,
        email :           parameter.email,
        isAdmin:          parameter.isAdmin,
        mobile :          parameter.mobile,
        phone :           parameter.phone,
        regId :           parameter.regId,
        userId :          parameter.userId,
        userName :        parameter.userName,
        userPassword:     parameter.userPassword,
      }});

      return res.data.value;

    },
    async changeManagerInfo(parameter: {
      accountStatus: string,
      area: string,
      areaKind: string,
      email: string,
      isAdmin: string,
      mobile: string,
      phone: string,
      updId: number,
      userName: string,
      updCompanyManagerSeq: number,
      userSeq: number,
      companyKind: string,
      userPassword?: string
    }, companyManagerSeq: number){
      

      const res = await useCustomFetch(`/api/v1/setting/manager/${companyManagerSeq}`, {method: Method.PUT, body: {
        accountStatus:          parameter.accountStatus,
        area:                   parameter.area,
        areaKind:               parameter.areaKind,
        email:                  parameter.email,
        isAdmin:                parameter.isAdmin,
        mobile:                 parameter.mobile,
        phone:                  parameter.phone,
        updId:                  parameter.updId,
        userName:               parameter.userName,
        updCompanyManagerSeq:   companyManagerSeq,
        userSeq:                parameter.userSeq,
        companyKind:            parameter.companyKind,
        userPassword:           parameter.userPassword
      }});

      return  res.data.value;
    },
    async changeUserKinds(updKind: string, updId: number, userSeqList: Number[], companyKind: string | undefined, companyManagerSeq: number| undefined){
      const res = await useCustomFetch(`/api/v1/setting/manager`, {method:Method.PUT, body: {
        updKind: updKind, 
        updId: updId, 
        userSeqList: userSeqList, 
        companyKind: companyKind, 
        updCompanyManagerSeq: companyManagerSeq
      }});

      return res.data.value;
    },
    async getSetEquipManager(parameter: {
      pageNo      : number,
      pageSize    : number,
      engSource?  : string,
      cityCode?   : string,
      sigunguCode?  : string,
      dongCode?     : string,
      prjKind?      : string,
      prjRegYear?   : string,
      searchKind?   : string,
      searchText?   : string,
      sort?         : string
    }){
      const res = await useCustomFetch(`/api/v1/setting/manager/equip`, {method: Method.GET, query: {
        pageNo        : parameter.pageNo,
        pageSize      : parameter.pageSize,
        engSource     : parameter.engSource,
        cityCode      : parameter.cityCode,
        sigunguCode   : parameter.sigunguCode,
        dongCode      : parameter.dongCode,
        prjKind       : parameter.prjKind,
        prjRegYear    : parameter.prjRegYear,
        searchKind    : parameter.searchKind,
        searchText    : parameter.searchText,
        sort          : parameter.sort
      }});

      return res.data.value;
    },
    async confirmAllEquipCompanyManager(setKind: 1 | 2,
      parameter: {
      userSeq             : number,
      engSource?          : string,
      cityCode?           : string,
      sigunguCode?        : string,
      dongCode?           : string,
      prjKind?            : string,
      prjRegYear?         : string,
      searchKind?         : string,
      searchText?         : string,
      companyKind?        : string,
      companyManagerSeq?  : string,
    }){
      const res = await useCustomFetch(`/api/v1/setting/manager/equip/${setKind}`, {method: Method.PUT, query: {
        userSeq            :  parameter.userSeq,
        engSource          : parameter.engSource,
        cityCode           : parameter.cityCode,
        sigunguCode        : parameter.sigunguCode,
        dongCode           : parameter.dongCode,
        prjKind            : parameter.prjKind,
        prjRegYear         : parameter.prjRegYear,
        searchKind         : parameter.searchKind,
        searchText         : parameter.searchText,
        companyKind        : parameter.companyKind,
        companyManagerSeq  : parameter.companyManagerSeq,
      }});

      return res.data.value;
    },
    async confirmSelectEquipCompanyManager(
      setKind: 1|2,
      companyKind: string,
      companyManagerSeq: number,
      updId: number,
      cidList: string[]
    ){
      const res = await useCustomFetch(`/api/v1/setting/manager/equip/${setKind}`, {method: Method.PUT, body: {
        companyKind: companyKind, 
        companyManagerSeq: companyManagerSeq, 
        updId: updId, 
        cidList: cidList
      }});

      return res.data.value;
    },
    async setUserTocSetting(data: any){
      const res = await useCustomFetch(`/api/v1/setting/personalization`, 
        {method: Method.POST, body: data});

      return res.data.value;
    },
    async getUserTocSettingImage(url: string){
      const res = await useCustomFetch(`/api/v1/setting/personalization/attach`, 
        {method: Method.GET, query: {filePath: url}});

      return res.data.value;
    },
    async getTocOwnerList(parameter: {
      pageNo       : number,
      pageSize     : number,
      cityCode?    : string,
      sigunguCode? : string,
      dongCode?    : string,
      searchKind?  : string,
      searchText?  : string
    }){

      const res = await useCustomFetch(`/api/v1/owners`, {method: Method.GET, query:{
          pageNo        : parameter.pageNo,
          pageSize      : parameter.pageSize,
          cityCode      : parameter.cityCode,
          sigunguCode   : parameter.sigunguCode,
          dongCode      : parameter.dongCode,
          // searchKind    : parameter.searchKind,
          ownerName     : parameter.searchText
      }});

      return res.data.value;
    },
    async getTocExcelOwnerList(parameter: {
      pageNo         : number,
      pageSize       : number,
      cityCode?      : string,
      sigunguCode?   : string,
      dongCode?      : string,
      accountStatus? : string,
      searchKind?    : string,
      searchText?    : string
    }){

      const res = await useCustomFetch(`/api/v1/system/owners`, {method: Method.GET, query:{
          pageNo        : parameter.pageNo,
          pageSize      : parameter.pageSize,
          cityCode      : parameter.cityCode,
          sigunguCode   : parameter.sigunguCode,
          dongCode      : parameter.dongCode,
          accountStatus : parameter.accountStatus,
          searchKind    : parameter.searchKind,
          searchText    : parameter.searchText
        }});

      return res.data.value;
    },
    async getTocExcelDownload(type: string){
      const res = await useCustomFetch(`/api/v1/system/excelsample`, 
        {method: Method.GET, query: {kind: type}});

      if(res.data.value){
        return res.data.value;
      } else {
        return res.error.value;
      }
    },
    async getTocEquipExcelDownload(type: string, engSource: string){
      const res = await useCustomFetch(`/api/v1/system/excel`, 
        {method: Method.GET, 
          query: {kind: type, engSource: engSource}});

      if(res.data.value){
        return res.data.value;
      } else {
        return res.error.value;
      }
    },
    async putTocExcel(type: string, files: any){
      let form = new FormData();

      let types = {
        kind: type
      }

      form.append('contents', JSON.stringify(types));
      form.append('files', files);

      const res = await useCustomFetch(`/api/v1/system/excel`, {method: Method.POST, body: form});

      return res;
    },

    async getRemsExcelData(
      remsId: string,
      password: string,
      startDate: string,
    ){
      try{
        const res = await useCustomFetch(`/api/v1/system/rems/excel`, {
          method: Method.GET,
          query: {
            remsId: remsId,
            password: password,
            startDate: startDate
          }
        });
  
        if(res.data.value){
          return res.data.value;
        } else {
          return res.error.value?.data.message
        }
      } catch(e){
        Swal.fire({
          html: `<p style="font-size: 15px;">오류가 발생하였습니다.<br>잠시 후 다시 시도해주세요.</p>`,
          confirmButtonColor: "#1949e9",
          confirmButtonText: "확인",
          width: "85%",
        })
      }
    },
    async setRemsExcelData(file: File){
      let formData = new FormData();

      formData.append('files', file);

      try{
        const res = await useCustomFetch('/api/v1/system/rems/excel', {method: Method.POST, body: formData});

        if(res.data.value){
          return res.data.value
        } else {
          return res.error.value?.data.meesage
        }
      }catch(e){
        Swal.fire({
          html: `<p style="font-size: 15px;">오류가 발생하였습니다.<br>잠시 후 다시 시도해주세요.</p>`,
          confirmButtonColor: "#1949e9",
          confirmButtonText: "확인",
          width: "85%",
        })
      }
    },
    async getWeatherData(
      cid?: string,
      dongCode?: string,
    ){
      try{
        const res = await useCustomFetch(`/api/v1/weather`, {method: Method.GET,
          query: {
            cid: cid,
            dongCode: dongCode
          }
        })

        if(res.data.value){
          return res.data.value;
        } else {
          throw new Error(res.error.value?.data.message);
        }
      } catch(e) {
        Swal.fire({
          html: `<p style="font-size: 15px;">오류가 발생하였습니다.<br>잠시 후 다시 시도해주세요.</p>`,
          confirmButtonColor: "#1949e9",
          confirmButtonText: "확인",
          width: "85%",
        })
      }
    },
    async getSolarRadAmt(dongCode: string|null|undefined) {
      try {
        const res = await useCustomFetch(`/api/v1/weather/solarRadiation`, {method: Method.GET, query: {dongCode: dongCode}});

        if(res.data.value){
          return res.data.value;
        } else {
          throw new Error(res.error.value?.data.message);
        }
      } catch(e){
        Swal.fire({
          html: `<p style="font-size: 15px;">오류가 발생하였습니다.<br>잠시 후 다시 시도해주세요.</p>`,
          confirmButtonColor: "#1949e9",
          confirmButtonText: "확인",
          width: "85%",
        })
      }
    },
    async setRemsIndiviData(file: File){
      
      try{
        const res = await useCustomFetch('');

        
      }catch(e){

      }
    },
    async getDesignTocSiteList(params: {
      pageNo: number,
      pageSize: number,
      cityCode?: string,
      sigunguCode?: string,
      dongCode?: string,
      prjKind?: string,
      buildingUsage?: string,
      prjRegYear?: string,
      engSource?: string,
      equipStatus?: string,
      eqType?: string,
      searchKind?: string,
      searchText?: string,
      sort?: string
    }){

      let searchKind = params.searchKind;
      let searchText = params.searchText;

      if(!searchKind){
        searchText = undefined;
      } else if(!searchText){
        searchKind = undefined;
      }
      try{
        const res = await useCustomFetch(`/api/v1/toc/equips`, {method: Method.GET, query: {
          pageNo: params.pageNo,
          pageSize: params.pageSize,
          cityCode: params.cityCode,
          sigunguCode: params.sigunguCode,
          dongCode: params.dongCode,
          prjKind: params.prjKind,
          buildingUsage: params.buildingUsage,
          prjRegYear: params.prjRegYear,
          engSource: params.engSource,
          equipStatus: params.equipStatus,
          eqType: params.eqType,
          searchKind: params.searchKind,
          searchText: params.searchText,
          sort: params.sort
        }});

        if(res.data.value){
          return res.data.value;
        } else {
          throw new Error(res.error.value?.data.message);
        }

      }catch(e){
        Swal.fire({
          html: `<p style="font-size: 15px;">오류가 발생하였습니다.<br>잠시 후 다시 시도해주세요.</p>`,
          confirmButtonColor: "#1949e9",
          confirmButtonText: "확인",
          width: "85%",
        })
      }

    },
    async getMapDashInfo(parentCode?: string, group?: string){
      try{
        const res = await useCustomFetch(`/api/v1/toc/mapdashs`, {method: Method.GET, query: {parentDongCode: parentCode, groupLevel: group}});

        if(res.data.value){
          return res.data.value;
        } else {
          throw new Error(res.error.value?.data.message);
        }
      } catch(e){
        Swal.fire({
          html: `<p style="font-size: 15px;">오류가 발생하였습니다.<br>잠시 후 다시 시도해주세요.</p>`,
          confirmButtonColor: "#1949e9",
          confirmButtonText: "확인",
          width: "85%",
        })
      }
    },
    async getEventMemo(eventId: string, eventStartDate?: string){
      try{
        const res = await useCustomFetch(`/api/v1/events/${eventId}/memos`, 
          {method: Method.GET, query: {eventStartDate: eventStartDate}});

          if(res.data.value){
            return res.data.value;
          } else {
            throw new Error(res.error.value?.data.message);
          }

      } catch(e){
        Swal.fire({
          html: `<p style="font-size: 15px;">오류가 발생하였습니다.<br>잠시 후 다시 시도해주세요.</p>`,
          confirmButtonColor: "#1949e9",
          confirmButtonText: "확인",
          width: "85%",
        })
      }
    },
    async setEventMemo(eventId: string, eventStartDate: string, type: 'create' | 'update' | 'delete', memoInfo?: any){
      try{

        let res;

        if(type === 'create'){
          let formData = new FormData();
          let data = {title: memoInfo.title, contents: memoInfo.contents};

          formData.append('contents', JSON.stringify(data));
          
          res = await useCustomFetch(`/api/v1/events/${eventId}/memos`, 
            {method: Method.POST, query: {eventStartDate: eventStartDate}, body: formData});
            
        } else if(type === 'update'){
          let formData = new FormData();
          let data = {title: memoInfo.title, contents: memoInfo.contents};

          formData.append('contents', JSON.stringify(data));

          res = await useCustomFetch(`/api/v1/events/${eventId}/memos`, 
            {method: Method.PUT, query: {eventStartDate: eventStartDate}, body: formData});

        } else if(type === 'delete'){
          res = await useCustomFetch(`/api/v1/events/${eventId}/memos`, 
            {method: Method.DELETE, query: {eventStartDate: eventStartDate}});
        }

        if(!res){
          throw new Error('오류가 발생하였습니다.');
        } else {
          return res.data.value;
        }

      } catch(e){
        Swal.fire({
          html: `<p style="font-size: 15px;">오류가 발생하였습니다.<br>잠시 후 다시 시도해주세요.</p>`,
          confirmButtonColor: "#1949e9",
          confirmButtonText: "확인",
          width: "85%",
        })
      }
    },
    async prodListExcel(parameter: {
      searchDateType: number,
      startDate: string,
      endDate: string,
      cityCode?: string,
      sigunguCode?: string,
      dongCode?: string,
      prjKind?: string,
      buildingUsage?: string,
      prjRegYear?: string,
      engSource?: string,
      searchKind?: string,
      searchText?: string,
      sort?: string,
    }){

      try{
        const res = await useCustomFetch(`/api/v1/prod/list/excel`, {method: Method.GET, query: {
          searchDateType: parameter.searchDateType,
          startDate: parameter.startDate,
          endDate: parameter.endDate,
          cityCode: parameter.cityCode,
          sigunguCode: parameter.sigunguCode,
          dongCode: parameter.dongCode,
          prjKind: parameter.prjKind,
          buildingUsage: parameter.buildingUsage,
          prjRegYear: parameter.prjRegYear,
          engSource: parameter.engSource,
          searchKind: parameter.searchKind,
          searchText: parameter.searchText,
          sort: parameter.sort,
        }});

        if(res.data.value){
          return res.data.value;
        } else {
          throw new Error(res.error.value?.data.message);
        }
      } catch(e){
        Swal.fire({
          html: `<p style="font-size: 15px;">오류가 발생하였습니다.<br>잠시 후 다시 시도해주세요.</p>`,
          confirmButtonColor: "#1949e9",
          confirmButtonText: "확인",
          width: "85%",
        })
      }
    },
    async setSitePersonalization(
      siteId: string,
      siteTitle?: string,
      siteLogoFile?: File,
    ){
      try{
        let formData = new FormData();

        let contents = {
          siteId: siteId,
          siteSysTitle: siteTitle ? siteTitle : null,
        };

        formData.append('contents', JSON.stringify(contents));
        if(siteLogoFile){
          formData.append('files', siteLogoFile);
        }

        const res = await useCustomFetch(`/api/v1/setting/site-personalization`, {method: Method.POST, body: formData});

        if(res.data.value){
          // 응답 필드명이 환경마다 다를 수 있어 방어적으로 처리
          const data: any = res.data.value;
          const nextSysTitle =
            data?.siteSysTitle ?? data?.siteTitle ?? data?.title ?? siteTitle ?? null;
          const nextLogoSeq =
            data?.sitelogoSeq ?? data?.siteLogoSeq ?? data?.attachSeq ?? null;
          const nextAttachPath =
            data?.attachPath ?? data?.filePath ?? data?.path ?? null;
          const nextAttachName =
            data?.attachName ?? data?.fileName ?? data?.name ?? null;
          const enabled =
            data?.enabled ?? data?.useYn ?? data?.use ?? data?.isEnabled ?? null;

          // 캐시 갱신(즉시 화면 반영용)
          const cacheRef = (this as any).sitePersonalizationCache;
          if (cacheRef?.value) {
            cacheRef.value[siteId] = {
              siteSysTitle: nextSysTitle,
              sitelogoSeq: nextLogoSeq,
              attachPath: nextAttachPath,
              attachName: nextAttachName,
              enabled: enabled,
            };
          }

          // 선택된 사이트일 경우에도 즉시 반영(타입 필드가 없을 수 있어 any로 merge)
          if ((this.selectSiteInfo as any)?.siteId === siteId) {
            this.selectSiteInfo = {
              ...(this.selectSiteInfo as any),
              siteSysTitle: nextSysTitle,
              sitelogoSeq: nextLogoSeq,
              attachPath: nextAttachPath,
              attachName: nextAttachName,
              personalizationEnabled: enabled,
            } as any;
          }
          return res.data.value;
        } else {
          throw new Error(res.error.value?.data.message);
        }
      }catch(e){
        Swal.fire({
          html: `<p style="font-size: 15px;">오류가 발생하였습니다.<br>잠시 후 다시 시도해주세요.</p>`,
          confirmButtonColor: "#1949e9",
          confirmButtonText: "확인",
          width: "85%",
        })
      }
    },
    async removeSitePersonalization(siteId: string){
      try{
        const res = await useCustomFetch(`/api/v1/setting/site-personalization`, {method: Method.DELETE, query: {siteId: siteId}});
     
        if(res.data.value){
          // 캐시 제거/초기화
          const cacheRef = (this as any).sitePersonalizationCache;
          if (cacheRef?.value) {
            delete cacheRef.value[siteId];
          }
          // 선택된 사이트일 경우에도 즉시 반영
          if ((this.selectSiteInfo as any)?.siteId === siteId) {
            this.selectSiteInfo = {
              ...(this.selectSiteInfo as any),
              siteSysTitle: null,
              sitelogoSeq: null,
              attachPath: null,
              attachName: null,
              personalizationEnabled: null,
            } as any;
          }
          return res.data.value;
        } else {
          throw new Error(res.error.value?.data.message);
        }
      }catch(e){
        Swal.fire({
          html: `<p style="font-size: 15px;">오류가 발생하였습니다.<br>잠시 후 다시 시도해주세요.</p>`,
          confirmButtonColor: "#1949e9",
          confirmButtonText: "확인",
          width: "85%",
        })
      }
    }
  },

})